import { For, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { useNavigate } from "@solidjs/router"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { ButtonV2 } from "@opencode-ai/ui/v2/button-v2"
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@opencode-ai/ui/v2/dialog-v2"
import { Icon } from "@opencode-ai/ui/v2/icon"
import { useGlobal } from "@/context/global"
import { useLanguage } from "@/context/language"
import { ServerConnection, useServer } from "@/context/server"
import { ensureRemoteSession, listArtifactsForPeriod, listDashboards, listFiles, openPeriodWorkflowChat } from "@/datox/api"
import { existingFactsheetOutputs, factsheetRequest, selectedFactsheetFiles } from "@/datox/factsheet-template"
import { Identifier } from "@/utils/id"
import { sessionHref } from "@/utils/session-route"
import "@/datox/report-templates.css"

const templates = [
  { id: "factsheet", title: "datox.factsheet.title", prompt: "datox.reportTemplates.factsheetPrompt" },
] as const

export function useDatoxReportTemplates(props: { projectID: string; periodID?: string; fileIDs: string[]; disabled: boolean }) {
  const language = useLanguage()
  const dialog = useDialog()
  const global = useGlobal()
  const server = useServer()
  const navigate = useNavigate()
  const [state, setState] = createStore({ busy: false })
  let mounted = true
  onCleanup(() => { mounted = false })

  const open = () => {
    if (!mounted || !props.periodID || props.disabled || state.busy) return
    const scope = { projectID: props.projectID, periodID: props.periodID, fileIDs: [...props.fileIDs] }
    dialog.show(() => {
      const [selection, setSelection] = createStore({ id: templates.length === 1 ? templates[0].id : "", messageID: Identifier.ascending("message"), runID: crypto.randomUUID(), request: undefined as { sessionID: string; text: string } | undefined, error: "" as "" | "sourceFilesChanged" | "error" })
      let visible = true
      onCleanup(() => { visible = false })
      const current = () => mounted && visible && props.projectID === scope.projectID && props.periodID === scope.periodID
      const generate = async () => {
        const template = templates.find((item) => item.id === selection.id)
        if (!template || state.busy || props.disabled || !current()) return
        setState("busy", true)
        setSelection("error", "")
        try {
          const files = selectedFactsheetFiles(scope, await listFiles(scope.projectID, scope.periodID))
          if (!current()) return
          if (!files) { setSelection("error", "sourceFilesChanged"); return }
          const session = await openPeriodWorkflowChat(scope.projectID, scope.periodID)
          if (!current()) return
          if (session.project_id !== scope.projectID || session.period_id !== scope.periodID) throw new Error("Workflow session scope mismatch")
          const remote = await ensureRemoteSession(scope.projectID, session.id)
          if (!current()) return
          if (!remote.remote_opencode_session_id) throw new Error("Missing remote session")
          if (!selection.request) {
            const [reports, dashboards] = await Promise.all([
              listArtifactsForPeriod(scope.projectID, scope.periodID, "report"),
              listDashboards(scope.projectID, scope.periodID, undefined, { strict: true }),
            ])
            if (!current()) return
            // Exact delivery retries must retain the same text even if outputs were saved meanwhile.
            setSelection("request", { sessionID: remote.remote_opencode_session_id, text: [language.t(template.prompt), factsheetRequest({
              projectID: scope.projectID, periodID: scope.periodID,
              periodName: (session.session_metadata?.period as { name?: string } | undefined)?.name ?? "",
              sessionID: session.id, runID: selection.runID, files,
              existingOutputs: existingFactsheetOutputs(scope, reports, dashboards),
            }, "report-and-dashboard")].join("\n\n") })
          }
          if (selection.request?.sessionID !== remote.remote_opencode_session_id) throw new Error("Template request session changed")
          const url = import.meta.env.VITE_DATOX_OPENCODE_SERVER_URL || ""
          const connection = server.list.find((item) => item.type === "http" && item.http.url.replace(/\/+$/, "") === url.replace(/\/+$/, ""))
            ?? server.add({ type: "http", http: { url, username: import.meta.env.VITE_DATOX_OPENCODE_USERNAME || "opencode", password: import.meta.env.VITE_DATOX_OPENCODE_PASSWORD || "" }, displayName: language.t("datox.workflows.chat.regularAgent") })
          if (!connection) throw new Error("Runtime connection unavailable")
          await global.ensureServerCtx(connection).sdk.api.session.prompt({
            sessionID: remote.remote_opencode_session_id,
            id: selection.messageID,
            agent: "custom-report-workflow-agent",
            text: selection.request.text,
          })
          if (!current()) return
          dialog.close()
          navigate(sessionHref(ServerConnection.key(connection), remote.remote_opencode_session_id))
        } catch {
          if (current()) setSelection("error", "error")
        } finally {
          if (mounted) setState("busy", false)
        }
      }
      return <Dialog fit class="report-templates-dialog" containerClass="report-templates-container">
        <DialogHeader hideClose={state.busy} closeLabel={language.t("common.close")}>
          <div class="report-templates-heading"><span class="report-templates-heading__icon" aria-hidden="true"><Icon name="report" /></span><DialogTitle>{language.t("datox.reportTemplates.title")}</DialogTitle></div>
        </DialogHeader>
        <DialogBody class="report-templates-body">
          <p class="report-templates-intro">{language.t("datox.reportTemplates.description")}</p>
          <p class="report-templates-intro">{language.t("datox.reportTemplates.updateHint")}</p>
          <fieldset disabled={state.busy} class="report-templates-options" aria-label={language.t("datox.reportTemplates.title")}>
            <For each={templates}>{(template) => <label class="report-template-card" data-selected={selection.id === template.id}>
              <input type="radio" name="report-template" class="report-template-card__radio" value={template.id} checked={selection.id === template.id} onChange={() => {
                if (selection.id !== template.id) setSelection({ id: template.id, messageID: Identifier.ascending("message"), runID: crypto.randomUUID(), request: undefined, error: "" })
              }} />
              <span class="report-template-card__preview" aria-hidden="true"><span class="report-template-card__page"><span /><span /><span /><span /></span></span>
              <span class="report-template-card__content">
                <span class="report-template-card__title">{language.t(template.title)}</span>
                <span class="report-template-card__description">{language.t("datox.reportTemplates.factsheetDescription")}</span>
                <span class="report-template-card__outputs"><span><Icon name="report" size="small" />{language.t("datox.pipeline.report")}</span><span><Icon name="grid-plus" size="small" />{language.t("datox.reportTemplates.editableDashboard")}</span></span>
              </span>
              <span class="report-template-card__check" aria-hidden="true"><Show when={selection.id === template.id}><Icon name="check" size="small" /></Show></span>
            </label>}</For>
          </fieldset>
          <div class="report-templates-scope"><Icon name="folder" size="small" /><span>{language.t("datox.reportTemplates.sourceFiles")}</span><strong>{scope.fileIDs.length}</strong></div>
          <Show when={!scope.fileIDs.length}><p class="report-templates-notice" role="status">{language.t("datox.reportTemplates.uploadFirst")}</p></Show>
          <Show when={selection.error}><p class="report-templates-error" role="alert">{language.t(selection.error === "sourceFilesChanged" ? "datox.reportTemplates.sourceFilesChanged" : "datox.reportTemplates.error")}</p></Show>
        </DialogBody>
        <DialogFooter>
          <span class="report-templates-chat-hint"><Icon name="chat" size="small" />{language.t("datox.reportTemplates.chatHint")}</span>
          <ButtonV2 variant="neutral" disabled={state.busy} onClick={() => dialog.close()}>{language.t("common.cancel")}</ButtonV2>
          <ButtonV2 variant="contrast" disabled={!selection.id || !scope.fileIDs.length || props.disabled || state.busy} aria-busy={state.busy} onClick={() => void generate()}>{language.t(state.busy ? "datox.reportTemplates.opening" : "datox.reportTemplates.generate")}</ButtonV2>
        </DialogFooter>
      </Dialog>
    })
  }

  return { open, busy: () => state.busy, disabled: () => !props.periodID || props.disabled || state.busy }
}
